import React from 'react';
import { motion } from 'motion/react';
import { Ticket, Trash2, AlertCircle, CheckCircle2, Lock } from 'lucide-react';
import { Voucher } from '../utils/api';
import { formatPrice } from '../data/products';

interface AppliedVoucherStatusProps {
  voucher: Voucher;
  isMinPurchaseMet: boolean;
  discount: number;
  subtotal: number;
  onRemove: () => void;
  className?: string;
  isPendingAuth?: boolean;
  onShowAuthModal?: () => void;
}

export const AppliedVoucherStatus: React.FC<AppliedVoucherStatusProps> = ({
  voucher,
  isMinPurchaseMet,
  discount,
  subtotal,
  onRemove,
  className = '',
  isPendingAuth = false,
  onShowAuthModal,
}) => {
  const minPurchase = voucher.conditions?.min_purchase || 0;
  const remaining = minPurchase - subtotal;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`relative border p-5 transition-all duration-300 ${
        isPendingAuth
          ? 'bg-amber-50/30 border-amber-200/80 shadow-sm'
          : isMinPurchaseMet 
            ? 'bg-paper border-ink/10 shadow-sm' 
            : 'bg-red-50/30 border-red-200/50 shadow-none'
      } ${className}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Icon */}
          <div className={`w-10 h-10 flex items-center justify-center shrink-0 transition-colors ${
            isPendingAuth
              ? 'bg-amber-100 text-amber-800'
              : isMinPurchaseMet 
                ? 'bg-brand-wine text-paper' 
                : 'bg-red-100 text-red-600'
          }`}>
            {isPendingAuth ? <Lock size={18} /> : isMinPurchaseMet ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[11px] font-black uppercase tracking-[0.1em] truncate ${
                isPendingAuth ? 'text-amber-950' : isMinPurchaseMet ? 'text-ink' : 'text-red-600'
              }`}>
                {voucher.code}
              </span>
              {isPendingAuth ? (
                <span className="text-[8px] font-black bg-amber-200/70 text-amber-900 border border-amber-300/60 px-1.5 py-0.5 uppercase tracking-widest shrink-0 flex items-center gap-0.5">
                  <Lock size={8} /> Tersimpan
                </span>
              ) : isMinPurchaseMet && (
                <span className="text-[8px] font-black bg-brand-wine/10 text-brand-wine px-1.5 py-0.5 uppercase tracking-widest shrink-0">
                  Aktif
                </span>
              )}
            </div>
            
            <p className="text-[10px] text-muted/60 font-bold uppercase tracking-wider truncate">
              {isPendingAuth
                ? `Potongan ${voucher.type === 'percentage' ? voucher.benefit_data.discount_value + '%' : formatPrice(discount)} • Masuk untuk aktifkan`
                : isMinPurchaseMet
                  ? voucher.type === 'gift' 
                    ? 'Hadiah Gratis Terpasang' 
                    : `Hemat ${voucher.type === 'percentage' ? voucher.benefit_data.discount_value + '%' : formatPrice(discount)}`
                  : `Tambah ${formatPrice(remaining)} lagi untuk mengaktifkan`
              }
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {isPendingAuth && onShowAuthModal && (
            <button
              onClick={onShowAuthModal}
              className="h-8 px-3 bg-brand-wine text-paper text-[9px] font-black uppercase tracking-wider hover:bg-brand-wine-deep transition-all"
            >
              Masuk
            </button>
          )}
          <button
            onClick={onRemove}
            className="p-2 text-muted/30 hover:text-brand-wine transition-colors group"
            aria-label="Lepas voucher"
          >
            <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </div>

      {/* Progress bar if min purchase not met */}
      {!isMinPurchaseMet && minPurchase > 0 && (
        <div className="mt-4">
          <div className="h-1 bg-red-100/50 w-full relative">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((subtotal / minPurchase) * 100, 100)}%` }}
              className="absolute h-full bg-red-400"
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};
